import React, { useCallback, useContext, useMemo, useState } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { COMMENT_OBJECT_TYPE, FEATURE_FLAG_NAME } from "common/constants";
import { generateDefaultValuesObject } from "common/utils/defaultValues";
import {
  GetPropertyForSubmissionQuery,
  GetSisdRuleDataQuery,
  GetSubmissionQuery,
} from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { TabGroupContainer } from "../Common/__styles__/TabGroup";
import TabGroup from "../Common/TabGroup";
import { Thread } from "../Threads/Thread";
import { FormComponent, FormDataType } from "./Form";

export const Content = ({
  submission,
  regeneratingPaths,
  handleRegenerateAIFields,
  setFormDisabled,
  propertyProp,
  property,
  serverProvidedFormData,
  onSubmit,
  account,
  submitFormRef,
}: {
  submission: NonNullable<GetSubmissionQuery["submission"]>;
  regeneratingPaths: Record<string, boolean>;
  handleRegenerateAIFields: (
    formData: FormDataType,
    paths: string[],
  ) => Promise<void>;
  setFormDisabled: (disabled: boolean) => void;
  propertyProp: {
    FIRMInfo?: GetSisdRuleDataQuery["FIRMInfo"];
  };
  property: GetPropertyForSubmissionQuery["property"];
  serverProvidedFormData: FormDataType | null;
  onSubmit: (formData: FormDataType) => Promise<void>;
  account: GetSisdRuleDataQuery["account"];
  submitFormRef: React.RefObject<HTMLButtonElement>;
}) => {
  const { user, admin, authorized, isFeatureEnabled } = useContext(AuthContext);

  const submissionCommentsEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.SUBMISSION_COMMENTS,
  );

  const canViewCommentThread = authorized({
    resource: RESOURCE_NAME.COMMENT,
    permission: "list",
  });

  const tabs = [
    { label: "Form", value: "form" },
    ...(submissionCommentsEnabled && canViewCommentThread ?
      [{ label: "Comments", value: "comments" }]
    : []),
  ];

  const [tab, setTab] = useState<string>("form");

  const dynamicDefaultValues = useMemo(() => {
    return generateDefaultValuesObject({
      property,
      user: user ?? admin,
    });
  }, [property, user, admin]);

  const handleOnChange = useCallback(
    ({
      isDirty,
      documentUploadsInProgress,
    }: {
      isDirty: boolean;
      documentUploadsInProgress: boolean;
    }) => {
      setFormDisabled(!isDirty || documentUploadsInProgress);
    },
    [setFormDisabled],
  );

  const canUpdateSubmission = authorized({
    resource: RESOURCE_NAME.SUBMISSION,
    permission: "update",
  });

  return (
    <>
      {submissionCommentsEnabled && canViewCommentThread && (
        <TabGroupContainer style={{ margin: `${spacing.xl} 0` }}>
          <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
        </TabGroupContainer>
      )}
      {tab === "form" && (
        <FormComponent
          readonly={!canUpdateSubmission}
          submissionType={{
            currentVersion: submission.submissionTypeVersion,
            modules: submission.submissionTypeVersion.submissionType.modules,
            id: submission.submissionTypeVersion.submissionType.id,
          }}
          existingSubmission={submission}
          relatedSubmissions={submission.relatedSubmissions}
          onSubmit={onSubmit}
          onChange={handleOnChange}
          submitFormRef={submitFormRef}
          dynamicDefaultValues={dynamicDefaultValues}
          account={account}
          property={propertyProp}
          loadingFormPaths={regeneratingPaths}
          regenerateAIFields={handleRegenerateAIFields}
          serverProvidedFormData={serverProvidedFormData ?? undefined}
        />
      )}
      {tab === "comments" && (
        <Thread
          objectId={submission.id}
          objectType={COMMENT_OBJECT_TYPE.SUBMISSION}
        />
      )}
    </>
  );
};
